import React from 'react';
import { Lightbulb, Layers, Hammer, ShieldCheck, Sparkles, Rocket } from 'lucide-react';

/**
 * CreationModeIndicator — Aria's six-stage creation flow.
 *
 * Architect Mandate · Feb 2026 · Creation Engine Cognitive Upgrade.
 * Visual badge that surfaces Aria's process so visitors see the spine
 * behind every creation: ENVISION → ARCHITECT → BUILD → VERIFY → POLISH → SHIP.
 *
 * Props:
 *   activeStage?: 'envision' | 'architect' | 'build' | 'verify' | 'polish' | 'ship'
 *                 (default: 'envision' — Aria is always at-the-ready in that stage)
 *   compact?:     boolean — if true renders a single-line micro-strip
 */

export const CREATION_STAGES = [
  { id: 'envision',  label: 'Envision',  icon: Lightbulb,    color: '#ff8fcf' },
  { id: 'architect', label: 'Architect', icon: Layers,       color: '#c64bff' },
  { id: 'build',     label: 'Build',     icon: Hammer,       color: '#00d8ff' },
  { id: 'verify',    label: 'Verify',    icon: ShieldCheck,  color: '#00ff88' },
  { id: 'polish',    label: 'Polish',    icon: Sparkles,     color: '#ffd700' },
  { id: 'ship',      label: 'Ship',      icon: Rocket,       color: '#ff5fa3' },
];

const CreationModeIndicator = ({ activeStage = 'envision', compact = false }) => {
  return (
    <div
      data-testid="creation-mode-indicator"
      role="group"
      aria-label="Aria Creation Mode — six stage flow"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? '0.35rem' : '0.5rem',
        padding: compact ? '0.4rem 0.7rem' : '0.5rem 0.9rem',
        background: 'rgba(255, 105, 180, 0.06)',
        border: '1px solid rgba(255, 105, 180, 0.28)',
        borderRadius: '999px',
        fontSize: compact ? '0.68rem' : '0.74rem',
        color: '#cbb8d4',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
      }}
    >
      <span style={{ color: '#ff8fcf', fontWeight: 700 }}>Creation Mode ·</span>
      {CREATION_STAGES.map((stage, i) => {
        const isActive = stage.id === activeStage;
        const Icon = stage.icon;
        return (
          <React.Fragment key={stage.id}>
            <span
              data-testid={`creation-stage-${stage.id}`}
              data-active={isActive ? 'true' : 'false'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                color: isActive ? stage.color : 'rgba(255,255,255,0.38)',
                fontWeight: isActive ? 700 : 500,
                transform: isActive ? 'scale(1.06)' : 'scale(1)',
                transition: 'transform 220ms ease, color 220ms ease',
                textShadow: isActive ? `0 0 8px ${stage.color}66` : 'none',
              }}
              title={`Stage ${i + 1}: ${stage.label}`}
            >
              <Icon size={compact ? 11 : 13} />
              {!compact && <span>{stage.label}</span>}
            </span>
            {i < CREATION_STAGES.length - 1 && (
              <span style={{ color: 'rgba(255,255,255,0.18)', fontSize: '0.6rem' }}>→</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default CreationModeIndicator;
